import { TSprite } from '@features/styles/sprite-sheet.service';
import { Rect, Vector2 } from '@typings/vectors';

const COLORS = {
  MAGENTA: [231, 67, 255, 255] as [number, number, number, number],
  BLUE: [0, 18, 255, 255] as [number, number, number, number],
  ORANGE: [242, 171, 41, 255] as [number, number, number, number],
};

const BOXES = {
  BOUNDS: COLORS.MAGENTA,
  PADDING: COLORS.BLUE,
  CONTENT: COLORS.ORANGE,
};

class ImageManipulator {
  private readonly canvas: HTMLCanvasElement;
  private readonly pixels: Uint8ClampedArray<ArrayBufferLike>;

  constructor(image: HTMLImageElement) {
    this.canvas = document.createElement('canvas');
    const ctx = this.canvas.getContext('2d');
    if (!ctx) throw new Error('No context');

    this.canvas.width = image.naturalWidth;
    this.canvas.height = image.naturalHeight;
    ctx.drawImage(image, 0, 0);

    this.pixels = ctx.getImageData(0, 0, this.canvas.width, this.canvas.height).data;
  }

  public expand(x: number, y: number) {
    const color = this.getPixel(x, y);
    if (color[3] === 0) throw new Error(`No color at (${x}, ${y})`);

    let dx = 0;
    let dy = 0;
    let pixel = this.getPixel(x + dx, y + dy);
    while (pixel[0] === color[0] && pixel[1] === color[1] && pixel[2] === color[2]) {
      dx++;
      pixel = this.getPixel(x + dx, y + dy);
    }
    dx--;
    pixel = this.getPixel(x + dx, y + dy);
    while (pixel[0] === color[0] && pixel[1] === color[1] && pixel[2] === color[2]) {
      dy++;
      pixel = this.getPixel(x + dx, y + dy);
    }
    dy--;
    return { from: [x, y] as Vector2, to: [x + dx, y + dy] as Vector2 };
  }

  public scanForColor(color: [number, number, number, number], rect: { from: Vector2; to: Vector2 }): Vector2 {
    for (let y = rect.from[1]; y <= rect.to[1]; y++) {
      for (let x = rect.from[0]; x <= rect.to[0]; x++) {
        const pixel = this.getPixel(x, y);
        if (pixel[0] === color[0] && pixel[1] === color[1] && pixel[2] === color[2]) {
          return [x, y] as Vector2;
        }
      }
    }
    throw new Error('Color not found');
  }

  public findFirstNonZero(position: Vector2): Vector2 {
    for (let y = position[1]; y < this.canvas.height; y++) {
      for (let x = position[0]; x < this.canvas.width; x++) {
        const pixel = this.getPixel(x, y);
        if (pixel[0] > 0) {
          return [x, y] as Vector2;
        }
      }
    }
    throw new Error('No non-zero pixel found');
  }

  public async getUrl(from: Vector2, to: Vector2): Promise<string> {
    const offscreenCanvas = document.createElement('canvas');
    const ctx = offscreenCanvas.getContext('2d');
    if (!ctx) throw new Error('No context');

    const width = to[0] - from[0] + 1;
    const height = to[1] - from[1] + 1;
    offscreenCanvas.width = width;
    offscreenCanvas.height = height;

    ctx.drawImage(this.canvas, from[0], from[1], width, height, 0, 0, width, height);

    return offscreenCanvas.toDataURL('image/webp');
  }

  private getPixel(x: number, y: number) {
    if (x < 0 || y < 0 || x >= this.canvas.width || y >= this.canvas.height) return [0, 0, 0, 0];

    const index = (y * this.canvas.width + x) * 4;
    return [this.pixels[index], this.pixels[index + 1], this.pixels[index + 2], this.pixels[index + 3]];
  }
}

export class Slicer {
  private readonly main: ImageManipulator;
  private readonly sizing: ImageManipulator;

  constructor(styleSheetImg: HTMLImageElement, styleSheetSizingImg: HTMLImageElement) {
    this.main = new ImageManipulator(styleSheetImg);
    this.sizing = new ImageManipulator(styleSheetSizingImg);
  }

  public async getSprite(topLeft: Vector2): Promise<TSprite> {
    const boxes = this.getBoxes(topLeft);
    const url = await this.main.getUrl(boxes.bounds.from, boxes.bounds.to);
    const normalizedBoxes = this.normalizeRelativeToBounds(boxes);
    const size = normalizedBoxes.bounds.to;
    const sprite: TSprite = {
      url,
      size: [size[0] + 1, size[1] + 1] as Vector2,
      paddingBox: normalizedBoxes.padding,
      contentBox: normalizedBoxes.content,
      scale: 1,
    };
    return sprite;
  }

  private normalizeRelativeToBounds(boxes: { bounds: Rect; padding: Rect; content: Rect }): {
    bounds: Rect;
    padding: Rect;
    content: Rect;
  } {
    const bounds = {
      from: [0, 0] as Vector2,
      to: [boxes.bounds.to[0] - boxes.bounds.from[0], boxes.bounds.to[1] - boxes.bounds.from[1]] as Vector2,
    };
    const padding = {
      from: [boxes.padding.from[0] - boxes.bounds.from[0], boxes.padding.from[1] - boxes.bounds.from[1]] as Vector2,
      to: [boxes.padding.to[0] - boxes.bounds.from[0], boxes.padding.to[1] - boxes.bounds.from[1]] as Vector2,
    };
    const content = {
      from: [boxes.content.from[0] - boxes.bounds.from[0], boxes.content.from[1] - boxes.bounds.from[1]] as Vector2,
      to: [boxes.content.to[0] - boxes.bounds.from[0], boxes.content.to[1] - boxes.bounds.from[1]] as Vector2,
    };
    return {
      bounds,
      padding,
      content,
    };
  }

  private getBoxes(position: Vector2) {
    const boundsBox = this.sizing.expand(position[0], position[1]);
    let paddingBox = undefined;

    try {
      const topleftPaddingBox = this.sizing.scanForColor(BOXES.PADDING, boundsBox);
      paddingBox = this.sizing.expand(topleftPaddingBox[0], topleftPaddingBox[1]);
    } catch {
      // do nothing
    }

    const topLeftContentBox = this.sizing.scanForColor(COLORS.ORANGE, paddingBox ?? boundsBox);
    const contentBox = this.sizing.expand(topLeftContentBox[0], topLeftContentBox[1]);
    paddingBox = paddingBox ?? contentBox;
    return { bounds: boundsBox, padding: paddingBox, content: contentBox };
  }
}
